import React, { Fragment, PureComponent, ReactNode } from 'react';
import Hotkeys from 'react-hot-keys';
import { SlideFromRightWrapper } from '../common/SlideFromRightWrapper';
import { backArrowIcon, bedIcon, closeIcon } from '../common/Icons';
import { AutoComplete, Form, FormInstance, Input, Radio, Select, notification } from 'antd';
import { Option } from 'antd/lib/mentions';
import { FAMILY_MEMBERS_NAME } from '../../constants';
import { getDoctorSelectorList } from '../../store/idbHandler';
import TextArea from 'antd/lib/input/TextArea';
import ProcedureAutoSuggest, {
    autoFocusProcedureInput,
} from '../prescriptionPad/leftPanel/ProcedureAutoSuggest';
import { Procedure } from '../../store/types/prescription';
import { Patient } from '../../store/types/patient';
import PatientDetails from './PatientDetails';
import AdmitPatientDivContainer from './AdmitPatientDivContainer';
import { captureError } from '../../pages/captureError';
import ipdAdmitPatient, { IpdPatient } from '../../api/ipdAdmitPatient';
import { track } from '../../tracking';
import {
    ADMISSION_CATEGORY,
    ADMIT_PATIENT_CONTAINER_ID,
    DEPARTMENT,
    INSURANCE_TYPE,
} from './constants';
import generateIPId from '../../api/generateIPId';
import getAdmissionAdvices from '../../api/getAdmissionAdvices';
import { his } from '../../pages/history';
import { IPD_LIST } from '../../routes/paths';
import { checkIfValidPhoneNumber } from '../../pages/utils';
import IpdBedSelect from './IpdBedSelect';
import { BedsEntity, RateplaninclusionsEntity } from '../../api/getIpdConfigData';
import { generateElementId } from '../../store/utils';
import AntSelect from '../prescriptionPad/rightPanel/workspace/padV2/AntSelect';
import { getIpdConfig } from '../../store/localstorage';

interface Values {
    [key: string]:
        | string
        | undefined
        | {
              inclusions: {
                  rate: number;
                  name: string;
                  id: string;
              }[];
              name: string;
              id: string;
          }
        | {
              name: string;
              id: string;
          };
}

interface Props {
    closeMe: () => void;
    patient: Patient;
}

interface State {
    procedures: Procedure[];
    isSubmitting: boolean;
    bedSelectorisVisible?: boolean;
    bed?: BedsEntity;
}

class AdmitPatientDrawer extends PureComponent<Props, State> {
    state: State = { procedures: [], isSubmitting: false };

    closeIcon = closeIcon('w-12 h-12 fill-darwin-neutral-1000');
    bedIcon = bedIcon();
    backArrow = backArrowIcon();

    initialValues: Values = {
        'admission-category': getIpdConfig()?.categories?.[0],
        'attendee-name': '',
        department: 'surgery',
        doctor: getDoctorSelectorList()[0]?.id || '',
        'insurance-number': '',
        'insurance-type': 'critical_illness',
        ipid: '',
        'is-patient-insurance-pre-approved': 'optional',
        'mobile-number': '',
        notes: '',
        'pre-approval-id': '',
        'referring-doctor': '',
        'relation-with-patient': undefined,
        'duration-value': '5',
        rateplan: undefined,
    };

    getRatePlans = (): {
        id: string;
        name: string;
        inclusions: Omit<RateplaninclusionsEntity, 'plan'>[];
    }[] => {
        const rateplans = getIpdConfig()?.rateplans;
        const rateplaninclusions = getIpdConfig()?.rateplaninclusions;
        return (
            rateplans?.map((rateplan) => {
                const items = rateplaninclusions?.filter(
                    (rateplaninclusion) => rateplaninclusion.plan === rateplan.id,
                );

                const totalPrice = items?.reduce((acc, item) => acc + item.rate, 0);

                return {
                    id: rateplan.id,
                    name: `${rateplan.name} -  ₹${totalPrice || '0'}`,
                    inclusions:
                        items?.map((t) => ({
                            id: t.id,
                            name: t.name,
                            rate: t.rate,
                        })) || [],
                };
            }) || []
        );
    };

    ratePlans = this.getRatePlans();
    categories = getIpdConfig()?.categories || [];

    toggleBedSelector = (bed?: BedsEntity, cat?: string): void => {
        this.setState({ bedSelectorisVisible: !this.state.bedSelectorisVisible, bed });
        const category = this.categories.find((i) => i.id === cat) || this.categories[0];
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        this.formRef?.current?.setFieldsValue({
            bedName: bed?.name || '',
            bed: bed?.id || '',
            'admission-category': category,
        });
    };

    async componentDidMount(): Promise<void> {
        if (!this.props.patient.id) {
            return;
        }
        const res = await getAdmissionAdvices(this.props.patient.id);
        if (res.success) {
            this.setState({ procedures: res.data?.[0]?.procedures || [] });
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            this.formRef?.current?.setFieldsValue({
                notes: res.data?.[0]?.notes || '',
            });
        }

        await this.generateIpid();
    }

    onSelect = (procedure: Procedure) => {
        const { procedures } = this.state;
        const newProcedures = [...procedures, procedure];
        this.setState({ procedures: newProcedures });
        autoFocusProcedureInput();
    };

    removeProcedure = (index: number) => {
        const { procedures } = this.state;
        const newProcedures = [...procedures];
        newProcedures.splice(index, 1);
        this.setState({ procedures: newProcedures });
    };

    onFinish = async (values: Values) => {
        const { patient, closeMe } = this.props;

        console.log('Received values:', values);
        this.setState({
            isSubmitting: true,
        });

        try {
            const ipdPatient: IpdPatient = {
                // to changes when select bed is added
                bed: (values['bed'] as string) || this.state.bed?.id || '',
                department: {
                    name: DEPARTMENT[(values['department'] as string) || ''],
                    id: (values['department'] as string) || '',
                },

                category: {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    id: values['admission-category']?.id,
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    name: values['admission-category']?.name,
                },
                refer: {
                    name: (values['referring-doctor'] as string) || '',
                    id: generateElementId((values['referring-doctor'] as string) || ''),
                },
                insurance: {
                    pre_approved_id: values['pre-approval-id'] as string,
                    pre_approved_amount: 0,
                    pre_approved: values['is-patient-insurance-pre-approved'] === 'yes',
                    number: values['insurance-number'] as string,
                    type: INSURANCE_TYPE[(values['insurance-type'] as string) || ''],
                },
                attendee: {
                    phone: {
                        n: (values['mobile-number'] as string) || '',
                        c: '+91',
                    },
                    name: (values['attendee-name'] as string) || '',
                    id: 'attendee-id',
                },
                patient: {
                    formData:
                        patient.formData?.map((f) => ({
                            label: f.label || '',
                            type: f.type || '',
                            key: f.key || '',
                            value: f.value || '',
                        })) || [],
                    profile: {
                        personal: {
                            gender: patient.personal.gender,
                            phone: patient.personal.phone,
                            age: {
                                // @todo: Fix iso srting
                                dob: new Date(patient.personal.age?.dob || '').toISOString(),
                            },
                            name: [patient.personal.name.f, patient.personal.name.l]
                                .filter(Boolean)
                                .join(' '),
                        },
                    },
                    poid: this.props.patient.poid || '',
                    uuid: this.props.patient.uuid || '',
                    id: this.props.patient.id || '',
                },
                date: new Date().toISOString(),
                ipid: (values['ipid'] as string) || '-',
                doc: {
                    name:
                        getDoctorSelectorList().find((i) => i.id === values['doctor'])?.personal
                            .name.f ||
                        '' +
                            getDoctorSelectorList().find((i) => i.id === values['doctor'])?.personal
                                .name.l,
                    id: (values['doctor'] as string) || '',
                },
                docid: (values['doctor'] as string) || '',
                duration: {
                    value: parseInt((values['duration-value'] as string) || '1'),
                    unit: 'days',
                },
                rateplan: (values['rateplan'] || {
                    inclusions: [],
                    name: '',
                    id: '',
                }) as {
                    inclusions: {
                        rate: number;
                        name: string;
                        id: string;
                    }[];
                    name: string;
                    id: string;
                },
            };
            const res = await ipdAdmitPatient(ipdPatient);
            if (!res.success) {
                throw new Error('Failed to admit patient' + res.message);
            }
            closeMe();
            notification.success({
                message: 'Patient admitted successfully',
                duration: 5,
            });
            his.push(IPD_LIST);
        } catch (error) {
            captureError(error as Error);
            track('ERROR', {
                on: 'ipdAdmitPatient',
                error: (error as Error).message,
            });
            notification.error({
                message: 'Error in ipd admit patient',
                duration: 5,
            });
        } finally {
            this.setState({
                isSubmitting: false,
            });
        }
    };

    formRef: React.Ref<FormInstance<{ [k: string]: string }>> = React.createRef();

    generateIpid = async (): Promise<void> => {
        const res = await generateIPId();
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        this.formRef?.current?.setFieldsValue({ ipid: res.data.ipid });
    };

    render(): ReactNode {
        const { closeMe, patient } = this.props;
        const { procedures, isSubmitting, bedSelectorisVisible } = this.state;
        const { closeIcon, bedIcon, backArrow, onSelect, removeProcedure, onFinish } = this;

        const doctorObject: { [k: string]: string } = {};
        getDoctorSelectorList().forEach((i) => {
            const { f, l } = i.personal.name;
            doctorObject[i.id] = `${f} ${l}`;
        });

        return (
            <Fragment>
                <IpdBedSelect
                    closeMe={this.toggleBedSelector}
                    isVisible={bedSelectorisVisible || false}
                />
                <Hotkeys keyName="esc" onKeyDown={closeMe}>
                    <SlideFromRightWrapper
                        closeMe={closeMe}
                        cssClass="w-80p select-none"
                        style={{ maxWidth: '800px' }}
                        modalOverlayStyle={{ zIndex: '100' }}
                    >
                        <div
                            className="w-full h-full flex flex-col bg-darwin-neutrals-50 overflow-hidden text-13"
                            id={ADMIT_PATIENT_CONTAINER_ID}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="px-16 py-12 flex items-center justify-between border-b border-darwin-neutral_new-200 bg-white">
                                <div className="flex items-center space-x-16">
                                    <button
                                        onClick={closeMe}
                                        className="flex items-center justify-center w-16 h-16"
                                    >
                                        {backArrow}
                                    </button>
                                    <p className="text-20">Admit Patient</p>
                                </div>
                                <button className="p-6" onClick={closeMe}>
                                    {closeIcon}
                                </button>
                            </div>

                            <Form
                                ref={this.formRef}
                                name="admit-patient"
                                wrapperCol={{ span: 8 }}
                                initialValues={this.initialValues}
                                onFinish={onFinish}
                                layout="vertical"
                                autoComplete="off"
                                className="flex-1 flex flex-col p-0 overflow-hidden"
                            >
                                <div className="flex-1 overflow-auto space-y-8 pb-144">
                                    <PatientDetails patient={patient} />

                                    <AdmitPatientDivContainer name="Attendee details">
                                        <div className="p-16 grid grid-cols-3 gap-x-36 gap-y-16">
                                            <Form.Item
                                                label={
                                                    <p className="text-darwin-neutrals-1000 font-700">
                                                        Mobile number
                                                    </p>
                                                }
                                                name="mobile-number"
                                                rules={[
                                                    {
                                                        validator: (_, value) => {
                                                            if (
                                                                checkIfValidPhoneNumber(
                                                                    '+91',
                                                                    value,
                                                                )
                                                            ) {
                                                                return Promise.resolve();
                                                            }
                                                            return Promise.reject(
                                                                new Error(
                                                                    'Please enter a valid mobile number!',
                                                                ),
                                                            );
                                                        },
                                                    },
                                                ]}
                                            >
                                                <Input
                                                    className="border border-darwin-neutral_new-200 rounded-8 overflow-hidden p-0 h-36"
                                                    prefix={
                                                        <p className="h-full flex items-center px-12 bg-darwin-neutrals-50 whitespace-nowrap">
                                                            +91
                                                        </p>
                                                    }
                                                    type="number"
                                                    placeholder="7740997399"
                                                    maxLength={10}
                                                    minLength={10}
                                                />
                                            </Form.Item>
                                            <Form.Item
                                                label={
                                                    <p className="text-darwin-neutrals-1000 font-700">
                                                        Attendee name
                                                    </p>
                                                }
                                                name="attendee-name"
                                                // rules={[
                                                //     {
                                                //         required: true,
                                                //         message: 'Please enter attendee name!',
                                                //     },
                                                // ]}
                                            >
                                                <Input
                                                    className="border border-darwin-neutral_new-200 rounded-8 overflow-hidden h-36"
                                                    // prefix={
                                                    //     <Select
                                                    //         style={{ width: 72 }}
                                                    //         className="h-full flex items-center bg-darwin-neutrals-50 whitespace-nowrap"
                                                    //         defaultValue={'-'}
                                                    //         showArrow={false}
                                                    //     >
                                                    //         {SALUTATION.map((i) => (
                                                    //             <Option value={i}>{i}</Option>
                                                    //         ))}
                                                    //     </Select>
                                                    // }
                                                    placeholder="Attendee Name"
                                                />
                                            </Form.Item>
                                            <Form.Item
                                                label={
                                                    <p className="text-darwin-neutrals-1000 font-700">
                                                        Relation with the patient
                                                    </p>
                                                }
                                                name="relation-with-patient"
                                                // rules={[
                                                //     {
                                                //         required: true,
                                                //         message:
                                                //             'Please select relation with the patient!',
                                                //     },
                                                // ]}
                                            >
                                                <Select
                                                    className="h-36 border border-darwin-neutral_new-200 rounded-8 overflow-hidden"
                                                    placeholder="Select relation"
                                                >
                                                    {FAMILY_MEMBERS_NAME.map((v) => (
                                                        <Option value={v.id}>{v.value}</Option>
                                                    ))}
                                                </Select>
                                            </Form.Item>
                                        </div>
                                    </AdmitPatientDivContainer>

                                    <AdmitPatientDivContainer name="Insurance details">
                                        <div className="p-16 grid grid-cols-3 gap-x-36 gap-y-16">
                                            <Form.Item
                                                label={
                                                    <p className="text-darwin-neutrals-1000 font-700">
                                                        Insurance type
                                                    </p>
                                                }
                                                name="insurance-type"
                                                // rules={[
                                                //     {
                                                //         required: true,
                                                //         message: 'Please select insurance type!',
                                                //     },
                                                // ]}
                                            >
                                                <Select
                                                    className="border border-darwin-neutral_new-200 rounded-8 overflow-hidden h-36"
                                                    placeholder="Select insurance type"
                                                >
                                                    {Object.entries(INSURANCE_TYPE).map(
                                                        ([key, value]) => (
                                                            <Option value={key}>{value}</Option>
                                                        ),
                                                    )}
                                                </Select>
                                            </Form.Item>
                                            <Form.Item
                                                label={
                                                    <p className="text-darwin-neutrals-1000 font-700">
                                                        Insurance number
                                                    </p>
                                                }
                                                name="insurance-number"
                                                // rules={[
                                                //     {
                                                //         required: true,
                                                //         message: 'Please enter insurance number!',
                                                //     },
                                                // ]}
                                            >
                                                <Input
                                                    className="border border-darwin-neutral_new-200 rounded-8 overflow-hidden h-36"
                                                    placeholder="Enter Insurance Number"
                                                />
                                            </Form.Item>
                                            <Form.Item
                                                label={
                                                    <p className="text-darwin-neutrals-1000 font-700">
                                                        Is patient insurance pre-approved?
                                                    </p>
                                                }
                                                name="is-patient-insurance-pre-approved"
                                                // rules={[
                                                //     {
                                                //         required: true,
                                                //         message: 'Please select any one!',
                                                //     },
                                                // ]}
                                            >
                                                <Radio.Group className="flex space-x-12">
                                                    <Radio.Button
                                                        className="h-36 flex items-center"
                                                        value="yes"
                                                    >
                                                        Yes
                                                    </Radio.Button>
                                                    <Radio.Button
                                                        className="h-36 flex items-center"
                                                        value="no"
                                                    >
                                                        No
                                                    </Radio.Button>
                                                </Radio.Group>
                                            </Form.Item>
                                            <Form.Item
                                                label={
                                                    <p className="text-darwin-neutrals-1000 font-700">
                                                        Pre-Approval ID
                                                    </p>
                                                }
                                                name="pre-approval-id"
                                                // rules={[
                                                //     {
                                                //         required: true,
                                                //         message: 'Please enter pre-approval id!',
                                                //     },
                                                // ]}
                                            >
                                                <Input
                                                    className="border border-darwin-neutral_new-200 rounded-8 overflow-hidden h-36"
                                                    placeholder="Enter Pre-Approval ID"
                                                />
                                            </Form.Item>
                                        </div>
                                    </AdmitPatientDivContainer>

                                    <AdmitPatientDivContainer name="Admission reason">
                                        <Fragment>
                                            <div className="p-16 pb-6 grid grid-cols-3 gap-x-36 gap-y-16">
                                                <Form.Item
                                                    label={
                                                        <p className="text-darwin-neutrals-1000 font-700">
                                                            Referring doctor
                                                        </p>
                                                    }
                                                    name="referring-doctor"
                                                    // rules={[
                                                    //     {
                                                    //         required: true,
                                                    //         message: 'Please select referring doctor!',
                                                    //     },
                                                    // ]}
                                                >
                                                    <AutoComplete
                                                        showSearch
                                                        className="border border-darwin-neutral_new-200 rounded-8 overflow-hidden h-36 capitalize"
                                                        placeholder="Start typing doctor name..."
                                                        filterOption={(input, option) =>
                                                            ((option?.children as unknown) as string)
                                                                .toLowerCase()
                                                                .includes(input.toLowerCase())
                                                        }
                                                    >
                                                        {Object.entries(doctorObject).map(
                                                            ([key, value]) => (
                                                                <AutoComplete.Option value={value}>
                                                                    {value}
                                                                </AutoComplete.Option>
                                                            ),
                                                        )}
                                                    </AutoComplete>
                                                </Form.Item>
                                                <Form.Item
                                                    label={
                                                        <p className="text-darwin-neutrals-1000 font-700">
                                                            Admission category
                                                        </p>
                                                    }
                                                    name="admission-category"
                                                    // rules={[
                                                    //     {
                                                    //         required: true,
                                                    //         message: 'Please select admission category!',
                                                    //     },
                                                    // ]}
                                                >
                                                    <AntSelect
                                                        className="border border-darwin-neutral_new-200 rounded-8 overflow-hidden h-36 w-full"
                                                        options={this.categories}
                                                        getValue={(o) => o.id}
                                                        getLabelToCompare={(o) => o.name}
                                                        placeholder="Select category"
                                                        setValue={(option) => {
                                                            console.log(option);
                                                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                                            // @ts-ignore
                                                            this.formRef?.current?.setFieldsValue({
                                                                'admission-category': option,
                                                            });
                                                        }}
                                                        showSearch
                                                        allowClear
                                                    />
                                                </Form.Item>
                                                <Form.Item
                                                    label={
                                                        <p className="text-darwin-neutrals-1000 font-700">
                                                            Notes
                                                        </p>
                                                    }
                                                    name="notes"
                                                    // rules={[
                                                    //     {
                                                    //         required: true,
                                                    //         message: 'Please enter notes!',
                                                    //     },
                                                    // ]}
                                                >
                                                    <TextArea
                                                        className="border border-darwin-neutral_new-200 rounded-8 overflow-hidden px-10 py-5"
                                                        placeholder="Notes from the doctor"
                                                    />
                                                </Form.Item>
                                                <Form.Item
                                                    label={
                                                        <p className="text-darwin-neutrals-1000 font-700">
                                                            Tentative duration
                                                        </p>
                                                    }
                                                    name="duration-value"
                                                    // rules={[
                                                    //     {
                                                    //         validator: (_, value) => {
                                                    //             if (
                                                    //                 checkIfValidPhoneNumber(
                                                    //                     '+91',
                                                    //                     value,
                                                    //                 )
                                                    //             ) {
                                                    //                 return Promise.resolve();
                                                    //             }
                                                    //             return Promise.reject(
                                                    //                 new Error(
                                                    //                     'Please enter a valid mobile number!',
                                                    //                 ),
                                                    //             );
                                                    //         },
                                                    //     },
                                                    // ]}
                                                >
                                                    <Input
                                                        className="border border-darwin-neutral_new-200 rounded-8 overflow-hidden p-0 pl-12 h-36"
                                                        suffix={
                                                            <p className="h-full flex items-center px-12 bg-darwin-neutrals-50 whitespace-nowrap">
                                                                days
                                                            </p>
                                                            // <Select
                                                            //     style={{ width: 72 }}
                                                            //     className="h-full flex items-center bg-darwin-neutrals-50 whitespace-nowrap"
                                                            //     defaultValue={
                                                            //         initialValues['duration-unit']
                                                            //     }
                                                            //     showArrow={false}
                                                            //     onChange={onDurationUnitChange}
                                                            // >
                                                            //     {['days', 'weeks'].map((i) => (
                                                            //         <Option value={i}>{i}</Option>
                                                            //     ))}
                                                            // </Select>
                                                        }
                                                        type="number"
                                                        placeholder="5"
                                                    />
                                                </Form.Item>
                                            </div>
                                            <div className="pt-6 p-16">
                                                <p className="text-darwin-neutrals-1000 font-700">
                                                    Procedures
                                                </p>
                                                <ProcedureAutoSuggest
                                                    onSelect={onSelect}
                                                    elements={[]}
                                                />
                                                {procedures.length > 0 && (
                                                    <div className="mt-12 flex space-x-8 overflow-auto whitespace-nowrap">
                                                        {procedures.map((i, index) => (
                                                            <div className="flex items-center space-x-12 border border-darwin-accent-bright-blue-800 bg-darwin-accent-bright-blue-100 rounded-8 px-12 py-8">
                                                                <p className="text-13 font-700 text-darwin-accent-bright-blue-800">
                                                                    {i.name}
                                                                </p>
                                                                <button
                                                                    onClick={() =>
                                                                        removeProcedure(index)
                                                                    }
                                                                >
                                                                    {closeIcon}
                                                                </button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </Fragment>
                                    </AdmitPatientDivContainer>

                                    <AdmitPatientDivContainer name="IPD Details">
                                        <div className="p-16 pb-6 grid grid-cols-3 gap-x-36 gap-y-16">
                                            <Form.Item
                                                label={
                                                    <p className="text-darwin-neutrals-1000 font-700">
                                                        Department
                                                    </p>
                                                }
                                                name="department"
                                                // rules={[
                                                //     {
                                                //         required: true,
                                                //         message: 'Please select department!',
                                                //     },
                                                // ]}
                                            >
                                                <Select
                                                    className="border border-darwin-neutral_new-200 rounded-8 overflow-hidden h-36"
                                                    placeholder="General"
                                                >
                                                    {Object.entries(DEPARTMENT).map(
                                                        ([key, value]) => (
                                                            <Option value={key}>{value}</Option>
                                                        ),
                                                    )}
                                                </Select>
                                            </Form.Item>
                                            <Form.Item
                                                label={
                                                    <p className="text-darwin-neutrals-1000 font-700">
                                                        Doctor
                                                    </p>
                                                }
                                                name="doctor"
                                                // rules={[
                                                //     {
                                                //         required: true,
                                                //         message: 'Please select doctor!',
                                                //     },
                                                // ]}
                                            >
                                                <Select
                                                    className="border border-darwin-neutral_new-200 rounded-8 overflow-hidden h-36"
                                                    placeholder="Dr. Raghunandan Gupta"
                                                    onChange={(value) => console.log(value)}
                                                >
                                                    {Object.entries(doctorObject).map(
                                                        ([key, value]) => (
                                                            <Option value={key}>{value}</Option>
                                                        ),
                                                    )}
                                                </Select>
                                            </Form.Item>
                                            <Form.Item
                                                label={
                                                    <p className="text-darwin-neutrals-1000 font-700">
                                                        IPID
                                                    </p>
                                                }
                                                name="ipid"
                                                rules={[
                                                    {
                                                        required: true,
                                                        message: 'Please enter IPID!',
                                                    },
                                                ]}
                                            >
                                                <Input
                                                    className="border border-darwin-neutral_new-200 rounded-8 overflow-hidden p-0 pl-12 h-36"
                                                    suffix={
                                                        <button
                                                            onClick={this.generateIpid}
                                                            className="h-full flex items-center px-12 bg-darwin-neutrals-50 whitespace-nowrap text-darwin-accent-bright-blue-800 font-700"
                                                        >
                                                            Generate
                                                        </button>
                                                    }
                                                    placeholder="Enter IPID"
                                                />
                                            </Form.Item>
                                        </div>
                                    </AdmitPatientDivContainer>

                                    <AdmitPatientDivContainer name="Bed and Rate plan">
                                        <div className="p-16 flex items-center justify-between">
                                            <div className="flex items-center space-x-10">
                                                <p>{bedIcon}</p>
                                                <Form.Item
                                                    name="bedName"
                                                    rules={[
                                                        {
                                                            required: true,
                                                            message: 'Please select bed',
                                                        },
                                                    ]}
                                                >
                                                    <Input
                                                        className="border border-darwin-neutral_new-200 rounded-8 overflow-hidden h-36"
                                                        disabled
                                                        placeholder="select bed"
                                                        value={this.state.bed?.name || 'select bed'}
                                                    />
                                                </Form.Item>
                                                {/* <p>No bed selected</p> */}
                                            </div>
                                            <div className="flex items-center space-x-16">
                                                {/* <button className="secondary-btn-v3 btn--small px-16 py-8">
                                                    View rate plans
                                                </button> */}
                                                <button
                                                    onClick={() => this.toggleBedSelector()}
                                                    className="primary-btn-v3 btn--small px-16 py-8"
                                                >
                                                    {this.state.bed ? 'Change' : 'Select'} bed
                                                </button>
                                            </div>
                                        </div>

                                        <div className="p-16 flex items-center justify-between">
                                            <div className="flex items-center space-x-10 w-full">
                                                <Form.Item
                                                    className="w-full"
                                                    name="rateplan"
                                                    rules={[
                                                        {
                                                            required: true,
                                                            message: 'Please Rate plan',
                                                        },
                                                    ]}
                                                    label={
                                                        <p className="text-darwin-neutrals-1000 font-700">
                                                            Rate Plan
                                                        </p>
                                                    }
                                                >
                                                    <AntSelect
                                                        className="border border-darwin-neutral_new-200 rounded-8 overflow-hidden h-36 w-full"
                                                        options={this.ratePlans}
                                                        getValue={(o) => o.id}
                                                        getLabelToCompare={(o) => o.name}
                                                        placeholder="Select rate plan"
                                                        setValue={(option) => {
                                                            console.log(option);
                                                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                                            // @ts-ignore
                                                            this.formRef?.current?.setFieldsValue({
                                                                rateplan: option,
                                                            });
                                                        }}
                                                        showSearch
                                                        allowClear
                                                    />
                                                </Form.Item>
                                                {/* <p>No bed selected</p> */}
                                            </div>
                                        </div>
                                    </AdmitPatientDivContainer>
                                </div>

                                <div className="w-full px-16 py-12 flex items-center justify-between border-t border-darwin-neutral_new-200 bg-white">
                                    <button
                                        className="font-700 text-darwin-accent-bright-blue-800"
                                        onClick={closeMe}
                                    >
                                        Cancel
                                    </button>
                                    <Form.Item style={{ margin: 0 }}>
                                        <button
                                            className="primary-btn-v3 btn--small flex items-center space-x-8 px-16 py-8 disabled:opacity-70 disabled:cursor-not-allowed"
                                            disabled={isSubmitting}
                                        >
                                            {isSubmitting && (
                                                <p>
                                                    <svg
                                                        className="w-15 h-15 fill-white animate-spin"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        viewBox="0 0 512 512"
                                                    >
                                                        <path d="M96 256c0-26.5-21.5-48-48-48S0 229.5 0 256s21.5 48 48 48S96 282.5 96 256zM108.9 60.89c-26.5 0-48.01 21.49-48.01 47.99S82.39 156.9 108.9 156.9s47.99-21.51 47.99-48.01S135.4 60.89 108.9 60.89zM108.9 355.1c-26.5 0-48.01 21.51-48.01 48.01S82.39 451.1 108.9 451.1s47.99-21.49 47.99-47.99S135.4 355.1 108.9 355.1zM256 416c-26.5 0-48 21.5-48 48S229.5 512 256 512s48-21.5 48-48S282.5 416 256 416zM464 208C437.5 208 416 229.5 416 256s21.5 48 48 48S512 282.5 512 256S490.5 208 464 208zM403.1 355.1c-26.5 0-47.99 21.51-47.99 48.01S376.6 451.1 403.1 451.1s48.01-21.49 48.01-47.99S429.6 355.1 403.1 355.1zM256 0C229.5 0 208 21.5 208 48S229.5 96 256 96s48-21.5 48-48S282.5 0 256 0z"></path>
                                                    </svg>
                                                </p>
                                            )}
                                            <p>Admit</p>
                                        </button>
                                    </Form.Item>
                                </div>
                            </Form>
                        </div>
                    </SlideFromRightWrapper>
                </Hotkeys>
            </Fragment>
        );
    }
}

export default AdmitPatientDrawer;
